// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import BillingAddressInfoDisplay, {
    BillingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import PickUpLocationAddressInfoDisplay from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PickUpLocationAddressInfoDisplay";
import ShippingAddressInfoDisplay, {
    ShippingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ShippingAddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import CheckoutSectionHeading from "@keller/App/Components/Checkout/CheckoutSectionHeading";
import BlockCard, { BlockCardStyles } from "@keller/App/Widgets/ContentBlock/BlockCard";
import { doNothing } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const quoteState = getQuoteState(state, state.pages.rfqConfirmation.quoteId);
    return {
        quoteState,
        fulfillmentMethod: state.context.session.fulfillmentMethod,
        billToState: getBillToState(state, quoteState.value?.billTo?.id),
        shipToState: getShipToState(state, quoteState.value?.shipTo?.id),
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        homePageUrl: getHomePageUrl(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface RfqConfirmationDetailsStyles {
    detailsGridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerGridContainer?: GridContainerProps;
    jobNameGridItem?: GridItemProps;
    jobNameHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    dateGridItem?: GridItemProps;
    dateLabelText?: TypographyPresentationProps;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToGridItem?: GridItemProps;
    shipToHeadingAndText?: SmallHeadingAndTextStyles;
    rightColumnGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    blockCard?: BlockCardStyles;
    dividerGridItem?: GridItemProps;
    billingAddress?: BillingAddressInfoDisplayStyles;
    shippingAddress?: ShippingAddressInfoDisplayStyles;
    gridItem?: GridItemProps;
}

export const rfqConfirmationDetailsStyles: RfqConfirmationDetailsStyles = {
    detailsGridContainer: {
        gap: 10,
        css: css`
            margin-bottom: 20px;
        `,
    },
    leftColumnGridItem: { width: [12, 12, 12, 6, 6] },
    leftColumnInnerGridContainer: { gap: 10 },
    jobNameGridItem: { width: [6, 6, 4, 4, 4] },
    jobNameHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    salesRepGridItem: { width: [6, 6, 4, 4, 4] },
    salesRepHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    statusGridItem: { width: [6, 6, 4, 4, 4] },
    statusHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    dateGridItem: {
        width: [6, 6, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    dateLabelText: { weight: "bold", size: 14 },
    customerGridItem: { width: 12 },
    customerHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    shipToGridItem: { width: 12 },
    shipToHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    rightColumnGridItem: { width: [12, 12, 12, 6, 6] },
    notesHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
};

const styles = rfqConfirmationDetailsStyles;

const RfqConfirmationDetails: FC<Props> = ({
    quoteState,
    fulfillmentMethod,
    billToState,
    shipToState,
    pickUpWarehouse,
    homePageUrl,
}) => {
    // XNG-Changes: add billTo and shipTo information
    const quote = quoteState.value;
    const billTo = billToState.value;
    const shipTo = shipToState.value;

    if (!quote || !billTo || !shipTo) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.detailsGridContainer}>
                <BlockCard
                    key="ContentBlock_QuoteConfirmation"
                    title={translate("Your Quote Has Been Placed.")}
                    description={siteMessage(
                        "Keller_QuoteConfirmation_OrderNumber",
                        quote.erpOrderNumber || quote.orderNumber,
                    ).toString()}
                    buttonVariant="primary"
                    buttonLabel={translate("Continue Shopping")}
                    link={{ value: homePageUrl, type: "Url" }}
                    size="medium"
                    extendedStyles={styles.blockCard}
                    buttonSizeVariant="medium"
                />
            </StyledWrapper>
            <GridContainer {...styles.detailsGridContainer}>
                <GridItem {...styles.leftColumnGridItem}>
                    <GridContainer {...styles.leftColumnInnerGridContainer}>
                        {/* XNG-Changes: customerPo is now used for jobName */}
                        {quote.properties.customerPo && (
                            <GridItem {...styles.jobNameGridItem}>
                                <SmallHeadingAndText
                                    heading={translate("Job Name")}
                                    text={quote.properties.customerPo}
                                    extendedStyles={styles.jobNameHeadingAndText}
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.salesRepGridItem} data-test-selector="rfqConfirmation_salesRep">
                            <SmallHeadingAndText
                                heading={translate("Sales Rep")}
                                text={quote.salespersonName}
                                extendedStyles={styles.salesRepHeadingAndText}
                            />
                        </GridItem>
                        <GridItem {...styles.statusGridItem}>
                            <SmallHeadingAndText
                                heading={translate("Status")}
                                text={quote.statusDisplay}
                                extendedStyles={styles.statusHeadingAndText}
                            />
                        </GridItem>
                        {quote.orderDate && (
                            <GridItem {...styles.dateGridItem}>
                                <Typography {...styles.dateLabelText}>{translate("Date Submitted")}</Typography>
                                <Typography as="p">
                                    <LocalizedDateTime
                                        dateTime={quote.orderDate}
                                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                    />
                                </Typography>
                            </GridItem>
                        )}
                        {quote.billTo && (
                            <GridItem {...styles.customerGridItem}>
                                <SmallHeadingAndText
                                    heading={translate("Customer")}
                                    text={quote.customerName}
                                    extendedStyles={styles.customerHeadingAndText}
                                />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    {quote.notes.trim().length > 0 && (
                        <SmallHeadingAndText
                            heading={translate("Notes")}
                            text={quote.notes}
                            extendedStyles={styles.notesHeadingAndText}
                        />
                    )}
                </GridItem>
            </GridContainer>
            <StyledWrapper {...styles.dividerGridItem} />
            {/* <XNG-Changes>: use BillingAndShippingInfo  */}
            <GridContainer>
                <CheckoutSectionHeading label={translate("Addresses")} />
                <GridItem {...styles.gridItem}>
                    <BillingAddressInfoDisplay
                        billTo={billTo}
                        // XNG-Changes: set doNothing
                        onEdit={doNothing}
                        extendedStyles={styles.billingAddress}
                    />
                </GridItem>
                {fulfillmentMethod === FulfillmentMethod.Ship && (
                    <GridItem {...styles.gridItem}>
                        <ShippingAddressInfoDisplay
                            shipTo={shipTo}
                            // XNG-Changes: set doNothing
                            onEdit={doNothing}
                            extendedStyles={styles.shippingAddress}
                        />
                    </GridItem>
                )}
                {fulfillmentMethod === FulfillmentMethod.PickUp && (
                    <GridItem {...styles.gridItem}>
                        <PickUpLocationAddressInfoDisplay
                            location={pickUpWarehouse!}
                            // XNG-Changes: set doNothing
                            onEdit={doNothing}
                        />
                    </GridItem>
                )}
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RfqConfirmationDetails),
    definition: {
        displayName: "Details",
        allowedContexts: ["RfqConfirmationPage"],
        fieldDefinitions: [],
        group: "RFQ Quote Confirmation",
    },
};

export default widgetModule;
